import "./Checkout.css";

import { useEffect, useRef, useState } from "react";

import { Link, useNavigate } from "react-router-dom";

import {
  FaHome,
  FaTruck,
  FaCreditCard,
  FaPaypal,
  FaMoneyBillWave,
  FaLock,
} from "react-icons/fa";

import api from "../../services/api";
import { useCart } from "../../context/CartContext";
import PayPalCheckout from "../../components/PayPalCheckout/PayPalCheckout";

const PENDING_KEY = "cervicaPaymentToVerify";

const PERSONAL_FIELDS = [
  {
    name: "first_name",
    label: "שם פרטי",
    type: "text",
    maxLength: 50,
    required: true,
  },
  {
    name: "last_name",
    label: "שם משפחה",
    type: "text",
    maxLength: 50,
    required: true,
  },
  {
    name: "email",
    label: "אימייל",
    type: "email",
    maxLength: 100,
    required: true,
  },
  {
    name: "phone",
    label: "טלפון",
    type: "tel",
    maxLength: 20,
    required: true,
  },
];

const ADDRESS_FIELDS = [
  {
    name: "city",
    label: "עיר",
    type: "text",
    maxLength: 100,
    required: true,
  },
  {
    name: "address",
    label: "רחוב ומספר בית",
    type: "text",
    maxLength: 255,
    required: true,
  },
  {
    name: "postal_code",
    label: "מיקוד",
    type: "text",
    maxLength: 20,
    required: false,
  },
];

const PAYMENT_OPTIONS = [
  {
    value: "visa",
    title: "כרטיס אשראי",
    subtitle: "Visa / Mastercard",
    icon: FaCreditCard,
    className: "credit-icon",
  },
  {
    value: "paypal",
    title: "PayPal",
    subtitle: "תשלום דרך חשבון PayPal",
    icon: FaPaypal,
    className: "paypal-icon",
  },
  {
    value: "cash",
    title: "מזומן",
    subtitle: "תשלום בעת קבלה או איסוף",
    icon: FaMoneyBillWave,
    className: "cash-icon",
  },
];

function readPendingPayment() {
  try {
    return sessionStorage.getItem(PENDING_KEY) || "";
  } catch {
    return "";
  }
}

function Checkout() {
  const navigate = useNavigate();

  const { cartItems, subtotal, clearCart } = useCart();

  const formRef = useRef(null);
  const requestBusy = useRef(false);
  const sessionOpen = useRef(false);
  const activePaypalOrderRef = useRef("");

  const [deliveryMethod, setDeliveryMethod] = useState("delivery");

  const [paymentMethod, setPaymentMethod] = useState("cash");

  const [deliveryPrice, setDeliveryPrice] = useState(null);

  const [vatRate, setVatRate] = useState(18);

  const [profileReady, setProfileReady] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [sessionActive, setSessionActive] = useState(false);

  const [paymentMessage, setPaymentMessage] = useState("");

  const [paymentToVerify, setPaymentToVerify] = useState(readPendingPayment);

  const pendingRef = useRef(paymentToVerify);

  const [confirmedTotal, setConfirmedTotal] = useState(null);

  const [user, setUser] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postal_code: "",
  });

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/login", { replace: true });
      return;
    }

    let active = true;

    Promise.allSettled([
      api.get("/profile"),
      api.get("/settings/site"),
    ]).then(([profileResult, settingsResult]) => {
      if (!active) return;

      if (profileResult.status === "fulfilled") {
        const data = profileResult.value.data;

        setUser({
          first_name: String(data.first_name || ""),
          last_name: String(data.last_name || ""),
          email: String(data.email || ""),
          phone: String(data.phone || ""),
          address: String(data.address || ""),
          city: String(data.city || ""),
          postal_code: String(data.postal_code || ""),
        });

        setProfileReady(true);
      } else {
        setPaymentMessage("טעינת הפרטים נכשלה. רעננו את העמוד או התחברו שוב.");
      }

      if (settingsResult.status === "fulfilled") {
        const settings = settingsResult.value.data || {};

        const rawPrice = settings.delivery_price;
        const price = Number(rawPrice);

        if (
          rawPrice !== null &&
          rawPrice !== undefined &&
          String(rawPrice).trim() !== "" &&
          Number.isFinite(price) &&
          price >= 0
        ) {
          setDeliveryPrice(price);
        }

        const rawVat = settings.vat_rate;
        const vat = Number(rawVat);

        if (
          rawVat !== null &&
          rawVat !== undefined &&
          String(rawVat).trim() !== "" &&
          Number.isFinite(vat) &&
          vat >= 0 &&
          vat < 100
        ) {
          setVatRate(vat);
        }
      }

      setIsLoading(false);
    });

    return () => {
      active = false;
    };
  }, [navigate]);

  const deliveryFee = deliveryMethod === "pickup" ? 0 : deliveryPrice;

  const total = deliveryFee === null ? null : Number(subtotal) + deliveryFee;

  const displayedTotal = confirmedTotal ?? total;

  // המחירים באתר כבר כוללים מע״מ.
  // לכן אנחנו רק מחשבים כמה מע״מ כלול בסכום ולא מוסיפים אותו שוב.
  const vatAmount =
    displayedTotal === null || vatRate <= 0
      ? 0
      : displayedTotal * (vatRate / (100 + vatRate));

  const totalBeforeVat =
    displayedTotal === null ? null : displayedTotal - vatAmount;

  const checkoutLocked =
    isSubmitting || sessionActive || Boolean(paymentToVerify);

  const hasRequiredDetails = Boolean(
    profileReady &&
    user.first_name.trim() &&
    user.last_name.trim() &&
    user.email.trim() &&
    user.phone.trim() &&
    (deliveryMethod === "pickup" ||
      (user.address.trim() && user.city.trim() && deliveryPrice !== null))
  );

  const canCheckout = !isLoading && hasRequiredDetails && cartItems.length > 0;

  const rememberPayment = (orderId) => {
    activePaypalOrderRef.current = orderId;
    pendingRef.current = orderId;
    setPaymentToVerify(orderId);

    try {
      if (orderId) {
        sessionStorage.setItem(PENDING_KEY, orderId);
      } else {
        sessionStorage.removeItem(PENDING_KEY);
      }
    } catch {
      // يبقى رقم العملية محفوظاً داخل الصفحة الحالية.
    }
  };

  const closePaymentSession = () => {
    sessionOpen.current = false;
    setSessionActive(false);
  };

  const buildOrderData = (method) => ({
    payment_method: method,
    delivery_method: deliveryMethod,

    delivery_address:
      deliveryMethod === "delivery" ? user.address.trim() : null,

    city: deliveryMethod === "delivery" ? user.city.trim() : null,

    postal_code: deliveryMethod === "delivery" ? user.postal_code.trim() : null,

    items: cartItems.map(({ product_id, quantity }) => ({
      product_id,
      quantity,
    })),
  });

  const validateCheckout = () => {
    if (!localStorage.getItem("token")) {
      navigate("/login");

      throw new Error("יש להתחבר לפני ביצוע הזמנה.");
    }

    if (pendingRef.current) {
      throw new Error("יש לבדוק את התשלום הקודם לפני הזמנה חדשה.");
    }

    if (!canCheckout) {
      throw new Error("יש למלא את הפרטים ולוודא שהסל אינו ריק.");
    }

    if (!formRef.current?.reportValidity()) {
      throw new Error("יש לתקן את הפרטים המסומנים בטופס.");
    }
  };

  const handleCreatePaypalOrder = async () => {
    if (requestBusy.current || sessionOpen.current) {
      throw new Error("תהליך תשלום כבר פתוח.");
    }

    try {
      validateCheckout();

      if (!["paypal", "visa"].includes(paymentMethod)) {
        throw new Error("יש לבחור אמצעי תשלום מקוון.");
      }

      requestBusy.current = true;
      sessionOpen.current = true;

      setIsSubmitting(true);
      setSessionActive(true);
      setPaymentMessage("");

      await api.put("/profile", user);

      const { data } = await api.post(
        "/orders/paypal/create",
        buildOrderData(paymentMethod)
      );

      if (!data.orderId) {
        throw new Error("לא התקבל מספר תשלום.");
      }

      // שומרים מיד את מזהה PayPal.
      // כך סגירה/רענון/לחיצה נוספת לא יוצרים הזמנה מקומית נוספת.
      rememberPayment(data.orderId);

      const serverTotal = Number(data.total_price);

      if (Number.isFinite(serverTotal) && serverTotal > 0) {
        setConfirmedTotal(serverTotal);
      }

      return {
        orderId: data.orderId,
      };
    } catch (error) {
      closePaymentSession();

      setPaymentMessage(
        error.response?.data?.message ||
          error.message ||
          "לא ניתן להתחיל את התשלום."
      );

      throw error;
    } finally {
      requestBusy.current = false;
      setIsSubmitting(false);
    }
  };

  const handleApprovePaypal = async ({ orderId }) => {
    if (requestBusy.current) return;

    if (typeof orderId !== "string" || !/^[A-Z0-9]{1,36}$/.test(orderId)) {
      throw new Error("Invalid PayPal order ID");
    }

    requestBusy.current = true;

    setIsSubmitting(true);
    closePaymentSession();
    setPaymentMessage("");

    rememberPayment(orderId);

    try {
      const { data } = await api.post(
        "/orders/paypal/" + encodeURIComponent(orderId) + "/capture",
        {}
      );

      if (data.paid !== true) {
        setPaymentMessage(data.message || "התשלום עדיין בבדיקה.");
        return;
      }

      if (!data.order_id) {
        throw new Error("Missing local order ID");
      }

      rememberPayment("");

      clearCart();

      navigate("/order-success?order=" + data.order_id);
    } catch (error) {
      if (
        error.response?.data?.retry_allowed === true ||
        error.response?.status === 403
      ) {
        rememberPayment("");
        setConfirmedTotal(null);
      }

      setPaymentMessage(
        error.response?.data?.message ||
          "לא ניתן לאמת את התשלום. לחצו על בדיקת התשלום הקודם."
      );

      throw error;
    } finally {
      requestBusy.current = false;
      setIsSubmitting(false);
    }
  };

  const handlePaypalError = () => {
    if (!requestBusy.current) {
      closePaymentSession();
    }

    setPaymentMessage(
      (previous) =>
        previous || "אירעה שגיאה בתשלום. בדקו את מצב ההזמנה לפני ניסיון נוסף."
    );
  };

  const handleCancelPaypal = async () => {
    const paypalOrderId = activePaypalOrderRef.current || pendingRef.current;

    closePaymentSession();

    if (!paypalOrderId) {
      setConfirmedTotal(null);
      setPaymentMessage("תהליך התשלום בוטל.");
      return;
    }

    setIsSubmitting(true);

    try {
      await api.post(
        "/orders/paypal/" + encodeURIComponent(paypalOrderId) + "/cancel",
        {},
      );

      rememberPayment("");
      setConfirmedTotal(null);
      setPaymentMessage("תהליך התשלום בוטל. ניתן לנסות שוב.");
    } catch (error) {
      // אם השרת אומר שהעסקה כבר שולמה, לא מוחקים את המזהה.
      // המשתמש יוכל ללחוץ על 'בדיקת התשלום הקודם'.
      if (error.response?.status === 409) {
        setPaymentMessage(
          "מצב התשלום השתנה. לחצו על בדיקת התשלום הקודם.",
        );
        return;
      }

      // כשל בביטול לא אמור ליצור עסקה חדשה. נשאיר את המזהה לבדיקה.
      setPaymentMessage(
        "לא ניתן לאשר את הביטול כרגע. לחצו על בדיקת התשלום הקודם.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (
      paymentMethod !== "cash" ||
      requestBusy.current ||
      sessionOpen.current ||
      pendingRef.current
    ) {
      return;
    }

    try {
      validateCheckout();

      requestBusy.current = true;
      setIsSubmitting(true);
      setPaymentMessage("");

      await api.put("/profile", user);

      const { data } = await api.post("/orders", buildOrderData("cash"));

      if (!data.order_id) {
        throw new Error("Missing order ID");
      }

      clearCart();

      navigate("/order-success?order=" + data.order_id);
    } catch (error) {
      setPaymentMessage(
        error.response?.data?.message ||
          "לא ניתן לאשר את ההזמנה. בדקו ברשימת ההזמנות לפני ניסיון נוסף."
      );
    } finally {
      requestBusy.current = false;
      setIsSubmitting(false);
    }
  };

  const handleUserChange = (event) => {
    const { name, value } = event.target;

    setConfirmedTotal(null);

    setUser((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const renderField = ({ name, label, ...attributes }) => (
    <input
      key={name}
      {...attributes}
      name={name}
      aria-label={label}
      placeholder={label}
      value={user[name]}
      onChange={handleUserChange}
      disabled={checkoutLocked || isLoading || !profileReady}
    />
  );

  return (
    <main className="checkout-page">
      <h1>סיום הזמנה</h1>

      <section className="checkout-layout">
        <form ref={formRef} className="checkout-form" onSubmit={handleSubmit}>
          <div className="form-card">
            <h2>פרטים אישיים</h2>

            {isLoading && <p>טוען פרטים...</p>}

            <div className="form-grid">{PERSONAL_FIELDS.map(renderField)}</div>
          </div>

          <div className="form-card">
            <h2>שיטת קבלה</h2>

            <div className="delivery-options">
              <button
                type="button"
                className={
                  deliveryMethod === "delivery"
                    ? "delivery active-delivery"
                    : "delivery"
                }
                disabled={checkoutLocked}
                onClick={() => {
                  setDeliveryMethod("delivery");
                  setConfirmedTotal(null);
                }}
              >
                <FaTruck />
                משלוח עד הבית
              </button>

              <button
                type="button"
                className={
                  deliveryMethod === "pickup"
                    ? "delivery active-delivery"
                    : "delivery"
                }
                disabled={checkoutLocked}
                onClick={() => {
                  setDeliveryMethod("pickup");
                  setConfirmedTotal(null);
                }}
              >
                <FaHome />
                איסוף עצמי
              </button>
            </div>

            {deliveryMethod === "delivery" ? (
              <>
                <div className="form-grid address-grid">
                  {ADDRESS_FIELDS.map(renderField)}
                </div>

                {!isLoading && deliveryPrice === null && (
                  <p role="alert">
                    לא ניתן לטעון את מחיר המשלוח. רעננו את העמוד או בחרו איסוף
                    עצמי.
                  </p>
                )}
              </>
            ) : (
              <div className="pickup-box">
                ניתן לאסוף את ההזמנה מהקליניקה בתיאום מראש לאחר אישור ההזמנה.
              </div>
            )}
          </div>

          <div
            className="form-card payment-card"
            style={{ overflow: "visible" }}
          >
            <div className="payment-heading">
              <div>
                <span className="section-label">תשלום מאובטח</span>

                <h2>בחירת אמצעי תשלום</h2>
              </div>

              <FaCreditCard className="payment-heading-icon" />
            </div>

            <div className="payment-options">
              {PAYMENT_OPTIONS.map(
                ({ value, title, subtitle, icon: Icon, className }) => (
                  <button
                    key={value}
                    type="button"
                    className={
                      paymentMethod === value
                        ? "payment active-payment"
                        : "payment"
                    }
                    aria-pressed={paymentMethod === value}
                    disabled={checkoutLocked}
                    onClick={() => {
                      setPaymentMethod(value);
                      setPaymentMessage("");
                      setConfirmedTotal(null);
                    }}
                  >
                    <span className={"payment-icon " + className}>
                      <Icon />
                    </span>

                    <span className="payment-text">
                      <strong>{title}</strong>
                      <small>{subtitle}</small>
                    </span>

                    <span className="payment-radio">
                      <span />
                    </span>
                  </button>
                )
              )}
            </div>

            <div className={"payment-box " + paymentMethod}>
              {paymentMethod === "cash" && (
                <>
                  <FaMoneyBillWave />
                  <span>התשלום במזומן בעת קבלת ההזמנה או באיסוף עצמי.</span>
                </>
              )}

              {paymentMethod === "visa" && (
                <>
                  <FaCreditCard />
                  <span>התשלום בכרטיס מתבצע בטופס המאובטח של PayPal.</span>
                </>
              )}

              {paymentMethod === "paypal" && (
                <>
                  <FaPaypal />
                  <span>התשלום מתבצע באמצעות חשבון PayPal.</span>
                </>
              )}
            </div>

            <div className="secure-payment-note">
              <FaLock />
              פרטי כרטיס האשראי אינם נשמרים באתר
            </div>

            {paymentMessage && (
              <p role="alert" style={{ lineHeight: 1.7 }}>
                {paymentMessage}
              </p>
            )}

            {paymentToVerify && (
              <button
                type="button"
                className="pay-btn"
                disabled={isSubmitting}
                onClick={() =>
                  handleApprovePaypal({
                    orderId: paymentToVerify,
                  }).catch(() => {})
                }
              >
                {isSubmitting ? "בודק תשלום..." : "בדיקת התשלום הקודם"}
              </button>
            )}

            {paymentMethod !== "cash" ? (
              <div className="paypal-checkout-container">
                {canCheckout ? (
                  <PayPalCheckout
                    paymentMethod={paymentMethod}
                    createOrder={handleCreatePaypalOrder}
                    onApprove={handleApprovePaypal}
                    onError={handlePaypalError}
                    onCancel={handleCancelPaypal}
                    disabled={isSubmitting || Boolean(paymentToVerify)}
                  />
                ) : (
                  <p dir="rtl">יש למלא את הפרטים ולוודא שהסל אינו ריק.</p>
                )}
              </div>
            ) : (
              <button
                className="pay-btn"
                type="submit"
                disabled={!canCheckout || checkoutLocked}
              >
                {isSubmitting ? "שולח הזמנה..." : "אישור הזמנה במזומן"}
              </button>
            )}
          </div>
        </form>

        <aside className="checkout-summary">
          <h2>סיכום הזמנה</h2>

          {cartItems.length === 0 && (
            <p>
              הסל ריק. <Link to="/cart">חזרה לסל</Link>
            </p>
          )}

          {cartItems.map((item) => (
            <div className="summary-row" key={item.product_id}>
              <span>
                {item.name} × {item.quantity}
              </span>

              <strong>
                ₪{(Number(item.price) * Number(item.quantity)).toFixed(2)}
              </strong>
            </div>
          ))}

          <div className="summary-row">
            <span>משלוח</span>

            <strong>
              {deliveryFee === null ? "טרם נקבע" : "₪" + deliveryFee.toFixed(2)}
            </strong>
          </div>

          {displayedTotal !== null && (
            <>
              <div className="summary-row vat-row">
                <span>מחיר לפני מע״מ</span>
                <strong>₪{totalBeforeVat.toFixed(2)}</strong>
              </div>

              <div className="summary-row vat-row">
                <span>מע״מ {vatRate}% (כלול במחיר)</span>
                <strong>₪{vatAmount.toFixed(2)}</strong>
              </div>
            </>
          )}

          <div className="summary-total">
            <span>סה״כ לתשלום</span>

            <strong>
              {displayedTotal === null ? "—" : "₪" + displayedTotal.toFixed(2)}
            </strong>
          </div>
        </aside>
      </section>
    </main>
  );
}

export default Checkout;
